import React from 'react';
import { paths } from '@src/constants/pathsEnum';
import featuredImage from './images/technology-stack-feature-image.jpg';
import weWorkWith from './images/we-work-with.svg';
import letsTalkImage from './images/lets-talk.svg';
import headerBackgroundImage from './images/header-background-image.svg';
import imageOnRight from '@images/technology-stack/meet-our-team.svg';
import { buttonType } from '@commons/button/Button';
import {
  p4,
  sonic,
  openVSwitch,
  tungstenFabric,
  cisco,
  juniperNetworks,
  paloAltoNetworks,
  aristaNetworks,
  huawei,
  f5,
  checkPoint,
  nokiaNetworks,
  hotjar,
  figma,
  react,
  angular,
  vueJS,
  redux,
  less,
  javaScript,
  typeScript,
  django,
  go,
  cPlusPlus,
  python,
  kafka,
  java,
  kotlin,
  nodeJS,
  miro,
  sketch,
  axure,
  zeplin,
  rust,
  c,
  dpdk,
  cypress,
  selenium,
  robotFramework,
  pytest,
  tRex,
  googleCloud,
  aws,
  azure2,
  ibmCloud,
  redhat2,
  openstack2,
  ubuntu,
  centOS,
  debian,
  openShift,
  kubernetes,
  docker,
  envoyProxy,
  istio,
  kvm,
  xen,
  etcd,
  mariaDB,
  postgreSQL,
  mongoDB,
  ansible,
  terraform,
  puppet,
  saltstack,
  circleCI,
  travisCI,
  bamboo,
  jenkins,
  zuul,
  spinnaker,
  gitlab1,
  vault,
  vagrant,
  helm,
  juju,
  groovy,
  git,
  fluentd,
  prometheus,
  grafana,
  elkStack,
  snowflake,
  apacheSpark,
  cassandra,
  hadoop,
  jupyter,
  scikitLearn,
  pyTorch,
  mLlib,
  mxnet,
  tensorFlow,
  xGBoost,
  adobeCreativeCloud,
} from '@images/logos-techstack';

import * as styles from './payload.module.scss';

export const SEOProps = {
  title: 'Our Tech Stack - What Technologies We Use | CodiLime',
  description:
    'At CodiLime, we help solve business problems with up-to-date and stable technologies to ensure the highest quality code. Learn about our core technologies.',
  featuredImage: `https://codilime.com${featuredImage}`,
};

export const headerProps = {
  title: (
    <>
      Our <br />
      Technology <br />
      Stack
    </>
  ),
  backgroundImage: headerBackgroundImage,
  backgroundImageAlt: 'Our Technology Stack',
  titleBorderWidth: styles.titleBorderWidth,
};

export const sectionUnderHeaderProps = {
  descriptionPartOne: (
    <>
      At CodiLime, <b>we help solve the business problems</b> of companies from demanding, high-technology industries.
      We know that our dedicated software and network engineering teams need to work with the best technologies
      available to provide nothing less than <b>code excellence.</b>
      <br className={styles.newLineWithMargin} /> <b>Let us help you with your software project.</b>
    </>
  ),
  contactButtonLabel: 'Contact Us',
  classNames: {
    innerWrapper: styles.underHeaderInnerWrapper,
    imageContainer: styles.underHeaderImageContainer,
  },
};

export const sliderWithClickableBlocksProps = {
  sectionProps: {
    title: 'How do we work with our tech-savvy partners?',
    subtitle: (
      <>
        Whether you need experts in a particular technology or are looking for a partner who will deliver a complete
        software product to your doorstep, we are here to help you. Check out how we can work together.
      </>
    ),
    id: 'how-we-work',
  },
  classNames: {
    section: styles.sliderWithClickableBlocksSection,
    innerWrapper: styles.sliderWithClickableBlocksInnerWrapper,
  },
};

export const layoutCustomProps = {
  classNames: { innerWrapperContactForm: styles.innerWrapperContactForm },
};

export const textImageSectionProps = {
  sectionProps: {
    title: 'The technology stack we work with',
    subtitle: (
      <>
        Every project requires a dedicated approach and a <b>carefully selected tech stack</b> to ensure that its scope
        will be delivered on time and the code will be spotless. Check out what technologies our engineers and
        developers work with.
      </>
    ),
    id: 'work-with',
  },
  image: weWorkWith,
  imageAlt: 'The technology stack we work with',
};

export const networkSolutionsProps = {
  title: (
    <>
      Network <br />
      solutions
    </>
  ),
  subtitle: (
    <>
      Networks are in our DNA. We have been designing and building resilient and secure network solutions for global
      telcos since 2011.
    </>
  ),
  id: 'network-solutions',
  rowsWithBlocks: [
    {
      categoryTitle: (
        <>
          Open source <br />
          networking
        </>
      ),
      blocks: [
        {
          src: p4,
          link: paths.SERVICES.P4,
          alt: 'P4 development services',
          iconMaxHeight: 58,
        },
        {
          src: sonic,
          alt: 'Software for Open Networking in the Cloud',
          iconMaxHeight: 47,
        },
        {
          src: openVSwitch,
          alt: 'Open vSwitch',
          link: 'https://codilime.com/expertise/software-product-engineering-sdn-nfv/#spe-open-v-switch',
          iconMaxHeight: 52,
        },
        {
          src: tungstenFabric,
          alt: 'Tungsten Fabric',
          link: 'https://codilime.com/expertise/software-product-engineering-sdn-nfv/#spe-tungsten-fabric',
        },
      ],
    },
    {
      categoryTitle: (
        <>
          Vendor <br />
          solutions
        </>
      ),
      blocks: [
        {
          src: cisco,
          alt: 'Cisco',
          iconMaxHeight: 51,
        },
        {
          src: juniperNetworks,
          alt: 'Juniper Networks',
          iconMaxWidth: 80,
        },
        {
          src: paloAltoNetworks,
          alt: 'Palo Alto Networks',
        },
        {
          src: aristaNetworks,
          alt: 'Arista Networks',
          iconMaxWidth: 80,
        },
        {
          src: huawei,
          alt: 'Huawei',
          iconMaxHeight: 60,
        },
        {
          src: f5,
          alt: 'F5',
          iconMaxHeight: 58,
        },
        {
          src: checkPoint,
          alt: 'Check Point',
          iconMaxWidth: 87,
        },
        {
          src: nokiaNetworks,
          alt: 'Nokia Networks',
          iconMaxWidth: 87,
        },
      ],
    },
  ],
};

export const softwareDevelopmentProps = {
  title: 'Software development',
  subtitle: (
    <>
      CodiLime&apos;s software engineering teams work with the best and future-proof technologies for front-end,
      back-end, or low-level programming-related projects.
    </>
  ),
  id: 'software-development',
  classNames: { section: styles.softwareDevelopmentSection },
  rowsWithBlocks: [
    {
      categoryTitle: <>UX Design</>,
      blocks: [
        {
          src: hotjar,
          alt: 'Hotjar',
          iconMaxWidth: 73,
        },
        {
          src: figma,
          alt: 'Figma',
          iconMaxWidth: 81,
        },
        {
          src: adobeCreativeCloud,
          alt: 'Adobe Creative Cloud',
          iconMaxHeight: 56,
        },
        {
          src: miro,
          alt: 'Miro',
          iconMaxWidth: 80,
        },
        {
          src: sketch,
          alt: 'Sketch',
          iconMaxWidth: 100,
        },
        {
          src: axure,
          alt: 'Axure',
          iconMaxWidth: 75,
        },
        {
          src: zeplin,
          alt: 'Zeplin',
          iconMaxHeight: 100,
          iconMaxWidth: 100,
        },
      ],
    },
    {
      categoryTitle: 'Frontend',
      blocks: [
        {
          src: react,
          link: paths.SERVICES.REACT,
          alt: 'React development services',
          iconMaxHeight: 65,
        },
        {
          src: angular,
          link: paths.SERVICES.ANGULAR,
          alt: 'Angular development services',
          iconMaxHeight: 56,
        },
        {
          src: vueJS,
          alt: 'Vue.js framework',
          iconMaxHeight: 52,
        },
        {
          src: redux,
          alt: 'Redux library',
          iconMaxWidth: 80,
        },
        {
          src: less,
          alt: 'Less',
          iconMaxHeight: 60,
          iconMaxWidth: 95,
        },
        {
          src: javaScript,
          alt: 'JavaScript language',
          iconMaxHeight: 57,
        },
        {
          src: typeScript,
          alt: 'TypeScript language',
          iconMaxWidth: 80,
        },
        {
          src: django,
          alt: 'Django framework',
          iconMaxWidth: 80,
        },
      ],
    },
    {
      categoryTitle: 'Backend',
      blocks: [
        {
          src: go,
          link: paths.SERVICES.GO,
          alt: 'Golang development services',
          iconMaxWidth: 79,
        },
        {
          src: cPlusPlus,
          link: paths.SERVICES.C_AND_CPP,
          alt: 'C/C++ development',
          iconMaxHeight: 52,
        },
        {
          src: python,
          link: paths.SERVICES.PYTHON,
          alt: 'Python development services',
          iconMaxWidth: 80,
        },
        {
          src: kafka,
          alt: 'Kafka platform',
          iconMaxHeight: 51,
        },
        {
          src: java,
          link: paths.SERVICES.JAVA,
          alt: 'Java development services',
          iconMaxHeight: 57,
        },
        {
          src: kotlin,
          alt: 'Kotlin language',
          iconMaxWidth: 75,
        },
        {
          src: nodeJS,
          alt: 'Node.js',
          iconMaxWidth: 80,
        },
      ],
    },
    {
      categoryTitle: 'Low-level',
      blocks: [
        {
          src: rust,
          link: paths.SERVICES.RUST,
          alt: 'Rust development services',
          iconMaxWidth: 80,
        },
        {
          src: c,
          link: paths.SERVICES.C_AND_CPP,
          alt: 'C/C++ development',
          iconMaxHeight: 52,
        },
        {
          src: dpdk,
          alt: 'Data Plane Development Kit',
          link: 'https://codilime.com/expertise/software-product-engineering-low-level-programming/#spe-dpdk',
          iconMaxWidth: 80,
        },
      ],
    },
    {
      categoryTitle: 'Testing',
      blocks: [
        {
          src: cypress,
          alt: 'Cypress',
          iconMaxWidth: 80,
        },
        {
          src: selenium,
          alt: 'Selenium',
          iconMaxWidth: 82,
        },
        {
          src: robotFramework,
          alt: 'Robot Framework',
        },
        {
          src: pytest,
          alt: 'pytest',
          iconMaxHeight: 62,
        },
        {
          src: tRex,
          alt: 'TRex',
          iconMaxWidth: 78,
        },
      ],
    },
  ],
};

export const cloudsPlatformsProps = {
  title: (
    <>
      Clouds/OS/
      <br />
      platforms
    </>
  ),
  subtitle: (
    <>
      When your project needs cloud expertise, the CodiLime teams are here to build and integrate your cloud solutions
      across private, public and hybrid cloud environments.
    </>
  ),
  id: 'clouds-os-platforms',
  rowsWithBlocks: [
    {
      categoryTitle: 'Public cloud',
      blocks: [
        {
          src: googleCloud,
          alt: 'Google Cloud Platform',
        },
        {
          src: aws,
          alt: 'Amazon Web Services',
          iconMaxHeight: 40,
        },
        {
          src: azure2,
          alt: 'Microsoft Azure',
          iconMaxWidth: 70,
        },
        {
          src: ibmCloud,
          alt: 'IBM Cloud',
        },
      ],
    },
    {
      categoryTitle: 'Private cloud',
      blocks: [
        {
          src: redhat2,
          alt: 'Red Hat',
          iconMaxWidth: 78,
        },
        {
          src: openstack2,
          alt: 'Open Stack',
          iconMaxWidth: 79,
        },
      ],
    },
    {
      categoryTitle: 'Operating systems',
      blocks: [
        {
          src: ubuntu,
          alt: 'Ubuntu',
          iconMaxHeight: 62,
        },
        {
          src: centOS,
          alt: 'CentOS',
          iconMaxWidth: 75,
        },
        {
          src: debian,
          alt: 'Debian',
          iconMaxHeight: 62,
        },
      ],
    },
    {
      categoryTitle: 'Cloud-native services',
      blocks: [
        {
          src: openShift,
          alt: 'OpenShift',
          iconMaxHeight: 65,
        },
        {
          src: kubernetes,
          alt: 'Kubernetes',
          link: 'https://codilime.com/expertise/software-product-engineering-cloud/#spe-kubernetes-openshift',
          iconMaxWidth: 85,
        },
        {
          src: docker,
          alt: 'Docker',
          iconMaxWidth: 77,
        },
        {
          src: envoyProxy,
          alt: 'Envoy Proxy',
          iconMaxWidth: 88,
        },
        {
          src: istio,
          alt: 'Istio',
          iconMaxHeight: 60,
        },
      ],
    },
    {
      categoryTitle: 'Virtualization',
      blocks: [
        {
          src: kvm,
          alt: 'Kernel-based Virtual Machine',
          iconMaxWidth: 78,
        },
        {
          src: xen,
          alt: 'Xen',
          iconMaxWidth: 80,
        },
      ],
    },
    {
      categoryTitle: 'Storage/database',
      blocks: [
        {
          src: etcd,
          alt: 'etcd',
          iconMaxWidth: 80,
        },
        {
          src: mariaDB,
          alt: 'MariaDB',
          iconMaxHeight: 60,
        },
        {
          src: postgreSQL,
          alt: 'PostgreSQL',
          iconMaxHeight: 55,
          iconMaxWidth: 95,
        },
        {
          src: mongoDB,
          alt: 'MongoDB',
        },
      ],
    },
  ],
};

export const devOpsProps = {
  title: 'DevOps',
  subtitle: (
    <>
      The DevOps culture and approach are strong in CodiLime&apos;s teams. We deliver value faster, more reliably, and
      cost-effectively.
    </>
  ),
  id: 'devOps',
  classNames: { section: styles.devOpsSection },
  rowsWithBlocks: [
    {
      categoryTitle: 'Automation',
      blocks: [
        {
          src: ansible,
          alt: 'Ansible',
        },
        {
          src: terraform,
          alt: 'Terraform',
        },
        {
          src: puppet,
          alt: 'Puppet',
        },
        {
          src: saltstack,
          alt: 'SaltStack',
          iconMaxWidth: 86,
        },
      ],
    },
    {
      categoryTitle: 'CI/CD',
      blocks: [
        {
          src: circleCI,
          alt: 'CircleCI',
          iconMaxWidth: 80,
        },
        {
          src: travisCI,
          alt: 'Travis CI',
          iconMaxWidth: 80,
        },
        {
          src: bamboo,
          alt: 'Bamboo',
          iconMaxWidth: 87,
        },
        {
          src: jenkins,
          alt: 'Jenkins',
          iconMaxWidth: 80,
        },
        {
          src: zuul,
          alt: 'Zuul',
          iconMaxWidth: 87,
        },
        {
          src: spinnaker,
          alt: 'Spinnaker',
          iconMaxHeight: 65,
        },
        {
          src: gitlab1,
          alt: 'GitLab',
          iconMaxWidth: 80,
        },
      ],
    },
    {
      categoryTitle: 'IaaC',
      blocks: [
        {
          src: vault,
          alt: 'Vault',
          iconMaxHeight: 43,
        },
        {
          src: vagrant,
          alt: 'Vagrant',
          iconMaxHeight: 55,
        },
        {
          src: helm,
          alt: 'Helm Package Manager',
          iconMaxHeight: 55,
        },
        {
          src: juju,
          alt: 'Juju controller',
          iconMaxHeight: 60,
        },
        {
          src: groovy,
          alt: 'Groovy language',
          iconMaxHeight: 63,
          iconMaxWidth: 97,
        },
        {
          src: git,
          alt: 'git',
          iconMaxHeight: 80,
          iconMaxWidth: 98,
        },
      ],
    },
    {
      categoryTitle: 'Monitoring',
      blocks: [
        {
          src: fluentd,
          alt: 'fluentd',
        },
        {
          src: prometheus,
          alt: 'Prometheus',
        },
        {
          src: grafana,
          alt: 'Grafana',
          iconMaxWidth: 80,
        },
        {
          src: elkStack,
          alt: 'ELK Stack',
          iconMaxHeight: 62,
        },
      ],
    },
  ],
};

export const dataProps = {
  title: 'Data services',
  subtitle: (
    <>
      Data engineering and data science make it possible to process, store and analyze a vast amount of data generated
      by users, software, and machines. We will help you manage your data and turn it into business insights.
    </>
  ),
  id: 'data-services',
  classNames: { innerWrapper: styles.dataInnerWrapper },
  rowsWithBlocks: [
    {
      categoryTitle: 'Data engineering',
      blocks: [
        {
          src: snowflake,
          alt: 'Snowflake',
        },
        {
          src: apacheSpark,
          alt: 'Apache Spark',
          iconMaxHeight: 62,
          iconMaxWidth: 92,
        },
        {
          src: cassandra,
          alt: 'Apache Cassandra',
          iconMaxHeight: 51,
        },
        {
          src: hadoop,
          alt: 'Apache Hadoop',
        },
        {
          src: kafka,
          alt: 'Apache Kafka',
          iconMaxHeight: 51,
        },
        {
          src: elkStack,
          alt: 'ELK Stack',
          iconMaxHeight: 62,
        },
      ],
    },
    {
      categoryTitle: 'Data science',
      blocks: [
        {
          src: jupyter,
          alt: 'Jupyter',
          iconMaxHeight: 90,
          iconMaxWidth: 100,
        },
        {
          src: scikitLearn,
          alt: 'scikit-learn',
        },
        {
          src: pyTorch,
          alt: 'PyTorch',
          iconMaxWidth: 80,
        },
        {
          src: mLlib,
          alt: 'MLlib Apache Spark',
          iconMaxHeight: 92,
        },
        {
          src: mxnet,
          alt: 'Apache MXNet',
          iconMaxWidth: 86,
        },
        {
          src: tensorFlow,
          alt: 'TensorFlow',
          iconMaxHeight: 60,
        },
        {
          src: xGBoost,
          alt: 'XGBoost DMLC',
          iconMaxWidth: 80,
        },
      ],
    },
  ],
};

export const contactButtonImageSectionProps = {
  sectionProps: {
    classNames: {
      section: styles.contactButtonImageSection,
      innerWrapper: styles.contactButtonImageSectionInnerWrapper,
    },
  },
  title: 'Do you see a technology you need?',
  buttonTitle: 'Contact Us',
  typeOfButton: buttonType.LIME,
  image: letsTalkImage,
  imageAlt: 'Get in touch and learn more about our technologies',
  isH2: true,
  classNames: {
    customContent: styles.contactButtonImageSectionCustomContent,
    imageContainer: styles.contactButtonImageSectionImageContainer,
  },
};

export const textButtonImageSectionProps = {
  title: 'Need more? Meet our R&D team!',
  description:
    'In the progressive technology environment, you need coders and thinkers to solve the most complex issues. At CodiLime we have a Research and Development team ready to jump into deep waters!',
  id: 'meet-our-team',
  imageData: {
    src: imageOnRight,
    alt: 'Learn more about the R&D team',
  },
  buttonData: {
    href: paths.SERVICES.R_D,
    label: 'Explore our R&D',
  },
};
